import { useQuery } from '@tanstack/react-query';
import type {
  AgentApiJobChangeProcessBoardsShow,
  AgentApiJobChangeProcessesRecord,
} from '@/types/serializers';
import { useState, useCallback, useRef } from 'react';
import type { JobChangeProcessBoardsRequest } from '../types/JobChangeProcessBoard';
import { buildBoardQueryParams, mergeRecords } from './useFetchJobChangeProcessBoardsDetails';

type UncategorizedRefetchOptions = {
  nextPage?: boolean;
  resetPage?: boolean;
};

export default function useFetchJobChangeProcessBoardsUncategorized(
  initialQuery?: JobChangeProcessBoardsRequest
) {
  const [filters, setFilters] = useState<JobChangeProcessBoardsRequest>(initialQuery ?? {});
  const [page, setPage] = useState(1);
  // queryFn は再取得時に最新のページを見る必要があるため、state と別に ref でも保持する
  const pageRef = useRef(1);
  const [version, setVersion] = useState(0);
  const accumulatedRecordsRef = useRef<AgentApiJobChangeProcessesRecord[]>([]);

  const filterKey = buildBoardQueryParams({ ...filters, page: undefined });

  // filters と page は filterKey / page として queryKey に含めている。
  // ref は「取得のたびに参照が変わり再取得が止まらなくなる」ため意図的に queryKey から外す
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const result = useQuery({
    queryKey: ['uncategorizedJobChangeProcesses', filterKey, page, version],
    queryFn: async () => {
      const currentPage = pageRef.current;
      const response = await fetch(
        `/agent/api/job_change_process_boards/uncategorized_job_change_processes.json${buildBoardQueryParams(
          { ...filters, page: currentPage }
        )}`
      );
      if (!response.ok) throw new Error('未分類の取得に失敗しました');
      const data: AgentApiJobChangeProcessBoardsShow = await response.json();

      accumulatedRecordsRef.current =
        currentPage === 1
          ? data.records
          : mergeRecords(accumulatedRecordsRef.current, data.records);

      return { ...data, records: accumulatedRecordsRef.current };
    },
  });

  const { data, isLoading, isFetching, refetch } = result;

  // 「読み込み済み件数 < 総件数」で判定する。ページ番号から算出すると、スクロールイベントの
  // 多発でページが先行したときに hasNextPage が落ちて残りが読み込めなくなる
  const hasNextPage = data ? data.records.length < data.total_count : false;

  const resetPage = useCallback(() => {
    accumulatedRecordsRef.current = [];
    pageRef.current = 1;
    setPage(1);
  }, []);

  const uncategorizedRefetch = useCallback(
    async (options?: UncategorizedRefetchOptions) => {
      if (options?.nextPage) {
        if (!hasNextPage || isFetching) return;
        pageRef.current += 1;
        setPage(pageRef.current);
        return;
      }
      if (options?.resetPage) {
        resetPage();
        setVersion((prev) => prev + 1);
        return;
      }
      await refetch();
    },
    [hasNextPage, isFetching, refetch, resetPage]
  );

  const updateFilters = useCallback(
    (newFilters: Partial<JobChangeProcessBoardsRequest>) => {
      resetPage();
      setFilters((prev) => ({ ...prev, ...newFilters }));
    },
    [resetPage]
  );

  const setKeyword = useCallback((keyword: string) => updateFilters({ keyword }), [updateFilters]);

  const setOrganizationUserIds = useCallback(
    (organization_user_ids: number[]) => updateFilters({ organization_user_ids }),
    [updateFilters]
  );

  return {
    data,
    isLoading,
    isFetching,
    setKeyword,
    setOrganizationUserIds,
    uncategorizedRefetch,
    hasNextPage,
    currentPage: page,
  };
}
